import { useState } from "react";
import { get, send } from "../api.js";
import { formatDate } from "../format.js";
import { useAsync } from "../useAsync.js";

export interface FamilyLessonRow {
  bookingId: string;
  startsAt: string;
  serviceName: string | null;
  tutorName: string | null;
  status: string;
}

export interface FamilyBookingNamesResponse {
  student: { id: string; fullName: string };
  names: { declaredName: string; count: number; reviewCount: number; firstAt: string; lastAt: string }[];
  ownName: { count: number } | null;
  unnamed: FamilyLessonRow[];
}

/**
 * A record that carries lessons booked for other names: usually a parent's
 * record holding their children's lessons, which the Hub keeps there so the
 * family's lessons and credits keep working until somebody says who is who.
 *
 * The tutor sorts it out here: add a child as a family member (their lessons
 * move across, past and future, with their notes), or say which single learner
 * the record is really for.
 */
export function FamilyMembers({ studentId, onChanged }: { studentId: string; onChanged: () => void }) {
  const state = useAsync(() => get<FamilyBookingNamesResponse>(`/admin/students/${studentId}/family-members`), [studentId]);
  const [mode, setMode] = useState<{ kind: "add"; declaredName: string } | { kind: "rename" } | null>(null);
  const [fullName, setFullName] = useState("");
  const [parentName, setParentName] = useState("");
  const [alsoKnownAs, setAlsoKnownAs] = useState("");
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const data = state.data;
  if (!data || data.names.length === 0) return null;

  function open(next: NonNullable<typeof mode>) {
    setMode(next);
    setProblem(null);
    setDone(null);
    setTicked(new Set());
    if (next.kind === "add") {
      setFullName(next.declaredName);
    } else {
      setFullName(data!.names.length === 1 ? data!.names[0]!.declaredName : "");
      setParentName(data!.student.fullName);
      setAlsoKnownAs("");
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!mode || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      if (mode.kind === "add") {
        const result = await send<{ moved: number; notesMoved: number; student: { fullName: string } }>(
          "POST", `/admin/students/${studentId}/family-members`,
          { fullName: fullName.trim(), declaredNames: [mode.declaredName], bookingIds: [...ticked] });
        setDone(`${result.student.fullName} added. ${result.moved} lesson${result.moved === 1 ? "" : "s"} and ${result.notesMoved} note${result.notesMoved === 1 ? "" : "s"} moved.`);
      } else {
        const aliases = alsoKnownAs.split(",").map(name => name.trim()).filter(Boolean);
        await send("POST", `/admin/students/${studentId}/rename-record`,
          { fullName: fullName.trim(), parentName: parentName.trim(), ...(aliases.length ? { alsoKnownAs: aliases } : {}) });
        setDone(`This record is now ${fullName.trim()}.`);
      }
      setMode(null);
      state.reload();
      onChanged();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not save that.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel" aria-labelledby="family-members-heading">
      <h2 id="family-members-heading">Family record</h2>
      <p className="small">
        Lessons on {data.student.fullName}'s record were booked for{" "}
        {data.names.map(name => `${name.declaredName} (${name.count})`).join(", ")}.
        They work as normal here. Add each child as a family member to give them their own record and history.
      </p>
      {done && <p className="small" role="status">{done}</p>}
      {!mode && (
        <div className="row">
          {data.names.map(name => (
            <button key={name.declaredName} type="button" className="quiet small"
              onClick={() => open({ kind: "add", declaredName: name.declaredName })}>
              Add {name.declaredName} as a family member
            </button>
          ))}
          {!data.ownName && (
            <button type="button" className="quiet small" onClick={() => open({ kind: "rename" })}>This record is for…</button>
          )}
        </div>
      )}
      {mode && (
        <form onSubmit={save}>
          {mode.kind === "add" ? (
            <>
              <p className="small">
                Every lesson booked as “{mode.declaredName}” moves to the new record, past and future, with its notes.
              </p>
              <label>Full name<input aria-label="Family member's full name" required maxLength={200} value={fullName}
                onChange={event => setFullName(event.target.value)} /></label>
              {data.unnamed.length > 0 && (
                <fieldset>
                  <legend className="small">Older lessons booked without a name. Tick any that were {mode.declaredName}'s:</legend>
                  {data.unnamed.map(lesson => (
                    <label key={lesson.bookingId} className="small">
                      <input type="checkbox" checked={ticked.has(lesson.bookingId)} onChange={event => {
                        const next = new Set(ticked);
                        if (event.target.checked) next.add(lesson.bookingId); else next.delete(lesson.bookingId);
                        setTicked(next);
                      }} />
                      {" "}{formatDate(lesson.startsAt)} · {lesson.serviceName ?? "Lesson"}{lesson.tutorName ? ` · ${lesson.tutorName}` : ""}
                      {lesson.status !== "approved" ? ` · ${lesson.status}` : ""}
                    </label>
                  ))}
                </fieldset>
              )}
            </>
          ) : (
            <>
              <p className="small">
                Rename this record to the learner it is really for. Add any other children as family members first.
              </p>
              <label>Learner's full name<input aria-label="Learner's full name" required maxLength={200} value={fullName}
                onChange={event => setFullName(event.target.value)} /></label>
              <label>Parent or contact name<input aria-label="Parent or contact name" maxLength={200} value={parentName}
                onChange={event => setParentName(event.target.value)} /></label>
              <label>Also booked as (optional, comma-separated)<input aria-label="Other names for this learner" maxLength={400}
                placeholder="e.g. Matt" value={alsoKnownAs} onChange={event => setAlsoKnownAs(event.target.value)} /></label>
            </>
          )}
          <div className="row" style={{ marginTop: 8 }}>
            <button disabled={busy || !fullName.trim()}>
              {busy ? "Saving…" : mode.kind === "add" ? "Add family member and move lessons" : "Rename record"}
            </button>
            <button type="button" className="quiet" disabled={busy} onClick={() => setMode(null)}>Cancel</button>
          </div>
          {problem && <p className="error small" role="alert">{problem}</p>}
        </form>
      )}
    </section>
  );
}
